
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BarChart2, ShoppingCart, DollarSign, UserPlus, MessageCircle, Pause, Bell, HelpCircle } from 'lucide-react';

// Define use case colors and styles
const useCaseStyles = {
  'Customer Support': {
    bgColor: '#E8F5E9',
    accentColor: '#4CAF50',
    icon: HelpCircle,
    gradient: 'from-green-50 to-green-100/50'
  },
  'Notifications': {
    bgColor: '#E3F2FD',
    accentColor: '#2196F3',
    icon: Bell,
    gradient: 'from-blue-50 to-blue-100/50'
  },
  'Onboarding': {
    bgColor: '#F3E5F5',
    accentColor: '#9C27B0',
    icon: UserPlus,
    gradient: 'from-purple-50 to-purple-100/50'
  },
  'Pre-Sales': {
    bgColor: '#FFF3E0',
    accentColor: '#FF9800',
    icon: ShoppingCart,
    gradient: 'from-orange-50 to-orange-100/50'
  },
  'Sales': {
    bgColor: '#FCE4EC',
    accentColor: '#E91E63',
    icon: DollarSign,
    gradient: 'from-pink-50 to-pink-100/50'
  },
  'Survey': {
    bgColor: '#FFEBEE',
    accentColor: '#F44336',
    icon: BarChart2,
    gradient: 'from-red-50 to-red-100/50'
  },
  // Add personality types
  'Empathetic': {
    bgColor: '#E1F5FE',
    accentColor: '#03A9F4',
    icon: HelpCircle,
    gradient: 'from-blue-50 to-blue-100/50'
  },
  'Friendly': {
    bgColor: '#FFF3E0',
    accentColor: '#FF9800',
    icon: UserPlus,
    gradient: 'from-orange-50 to-orange-100/50'
  },
  'Custom': {
    bgColor: '#F3E5F5',
    accentColor: '#9C27B0',
    icon: MessageCircle,
    gradient: 'from-purple-50 to-purple-100/50'
  }
};

// Card style variants
const cardVariants = {
  hover: {
    scale: 1.03,
    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    transition: {
      duration: 0.2,
      ease: "easeInOut",
    },
  },
  tap: {
    scale: 0.98,
    transition: {
      duration: 0.1,
      ease: "easeInOut",
    },
  },
};

// Card content animation variants
const contentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.3
    }
  }
};

// Enhanced card animations
const enhancedCardVariants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  },
  hover: {
    scale: 1.03,
    y: -5,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  },
  tap: {
    scale: 0.98,
    transition: {
      duration: 0.1
    }
  }
};

// Animation variants
const loadingVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: {
    opacity: 1,
    scale: 1,
    rotate: 360,
    transition: {
      duration: 0.4,
      ease: "easeOut"
    }
  }
};

const AgentCard = ({ agent, onEdit }) => {
  // Get the style based on the exact use case name
  const style = useCaseStyles[agent.type] || {
    bgColor: '#F3F4F6',
    accentColor: '#6B7280',
    icon: HelpCircle,
    gradient: 'from-gray-50 to-gray-100/50'
  };
  const isActive = true;

  return (
    <motion.div
      className="relative w-72 rounded-[2rem] overflow-hidden shadow-lg transition-all duration-300"
      style={{
        backgroundColor: style.bgColor,
        boxShadow: `0 4px 24px ${style.accentColor}15`
      }}
      variants={cardVariants}
      whileHover={{
        scale: 1.02,
        boxShadow: `0 8px 32px ${style.accentColor}25`
      }}
      whileTap={{ scale: 0.98 }}
      layout
    >
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${style.accentColor}10, transparent)`
          }}
        />
      </div>

      {/* Card Content */}
      <div className="relative z-10 p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <motion.div 
              className="w-12 h-12 rounded-2xl flex items-center justify-center"
              style={{
                background: style.accentColor,
                boxShadow: `0 4px 24px ${style.accentColor}30`
              }}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              {React.createElement(style.icon, { 
                className: "w-6 h-6 text-white"
              })}
            </motion.div>
            <div className="text-left">
              <h3 className="text-gray-800 font-bold text-lg tracking-tight">{agent.name}</h3>
              <p style={{ color: style.accentColor }} className="text-sm font-medium">{agent.type}</p>
            </div>
          </div>
          {/* Status Indicator */}
          <div className="relative group">
            <div 
              className={`w-3.5 h-3.5 rounded-full ${
                isActive ? 'bg-green-400' : 'bg-gray-400'
              } ring-4 ring-white/40 shadow-lg`}
            >
              <div className={`absolute inset-0 rounded-full ${
                isActive ? 'bg-green-400' : 'bg-gray-400'
              } animate-ping opacity-50`} />
            </div>
            <div className="absolute top-0 right-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs py-1 px-2 rounded-md -translate-y-full whitespace-nowrap">
              {isActive ? 'Active' : 'Inactive'}
            </div>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm leading-relaxed mb-6 line-clamp-2">{agent.description}</p>

        {/* Progress Section */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-700 text-sm font-semibold">Performance</span>
            <span className="text-gray-700 text-sm font-medium">{agent.stats.conversionRate}</span>
          </div>
          <div className="h-2 bg-white/50 backdrop-blur-sm rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{
                background: style.accentColor
              }}
              initial={{ width: 0 }}
              animate={{ width: `${parseInt(agent.stats.conversionRate)}%` }}
              transition={{ duration: 1.5, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div 
            className="rounded-2xl p-4"
            style={{
              backgroundColor: 'white',
              border: `1px solid ${style.accentColor}20`
            }}
          >
            <p className="text-2xl font-bold" style={{ color: style.accentColor }}>
              {agent.stats.callsThisMonth}
            </p>
            <p className="text-gray-600 text-xs font-medium mt-1">Calls This Month</p>
          </div>
          <div 
            className="rounded-2xl p-4"
            style={{
              backgroundColor: 'white',
              border: `1px solid ${style.accentColor}20`
            }}
          >
            <p className="text-2xl font-bold" style={{ color: style.accentColor }}>
              {agent.stats.conversionRate}
            </p>
            <p className="text-gray-600 text-xs font-medium mt-1">Conversion Rate</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center">
          <motion.button
            className="flex items-center px-5 py-2.5 rounded-xl text-white font-medium text-sm shadow-lg"
            style={{
              background: style.accentColor
            }}
            whileHover={{ 
              scale: 1.02,
              boxShadow: `0 8px 20px ${style.accentColor}30`
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
            onClick={() => onEdit(agent.id)}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            Edit Agent
          </motion.button>
          <motion.button
            className="flex items-center px-5 py-2.5 rounded-xl text-gray-700 font-medium text-sm shadow-lg"
            style={{
              backgroundColor: 'white',
              border: `1px solid ${style.accentColor}20`
            }}
            whileHover={{ 
              scale: 1.02,
              backgroundColor: `${style.accentColor}05`
            }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <Pause className="w-4 h-4 mr-2" />
            Pause
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

const CreateAgentCard = ({ onAddAgent }) => {
  return (
    <Link to="/ai-agents/create">
      <motion.div
        className="relative w-72 rounded-[2rem] bg-blue-300 overflow-hidden shadow-lg hover:shadow-blue-500/20 transition-all duration-300"
        variants={cardVariants}
        whileHover="hover"
        whileTap="tap"
        layout
      >
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-blue-200" />
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-blue-10 to-blue" />
        </div>
        
        <motion.div className="relative z-10 h-[400px] flex flex-col items-center justify-center p-8 text-center">
          <motion.div 
            className="w-16 h-16 rounded-2xl flex items-center justify-center  mb-6"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 17 }}
          >
            <svg className="w-14 h-14 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </motion.div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Create New Agent</h2>
          <p className="text-gray-500 text-sm mb-8">click to create a new AI agent to enhance your workspace</p>
       {/*  <motion.button
  className="px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shadow-sm transition"
  whileHover={{ scale: 1.03 }}
  whileTap={{ scale: 0.97 }}
  transition={{ type: "spring", stiffness: 300, damping: 20 }}
>
  Get Started
</motion.button> */}

        </motion.div>
      </motion.div>
    </Link>
  );
};

const AIAgents = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState(() => {
    const savedAgents = localStorage.getItem('agents');
    return savedAgents ? JSON.parse(savedAgents) : [];
  });

  const handleEdit = (id) => {
    navigate(`/ai-agents/edit/${id}`);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this agent?')) {
      const updatedAgents = agents.filter(agent => agent.id !== id);
      setAgents(updatedAgents);
      localStorage.setItem('agents', JSON.stringify(updatedAgents));
    }
  };

  const handleAddAgent = (newAgent) => {
    const updatedAgents = [...agents, newAgent];
    setAgents(updatedAgents);
    localStorage.setItem('agents', JSON.stringify(updatedAgents));
  };

  return (
    <div className="pt-8 pl-4">
      <h1 className="text-4xl font-bold mb-14">My Agents</h1>
      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 "
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, staggerChildren: 0.1 }}
      >
        <CreateAgentCard onAddAgent={handleAddAgent} />
        {agents.map((agent) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))}
      </motion.div>
    </div>
  );
};

export default AIAgents;
